<template>
  <!-- One sidebar instead of the top bar and side menu (US 9372) -->
  <div id="app-sidebar" :class="{ 'is-partition': !!getClient.parent }">

    <!-- Mobile: slim top bar with menu, workspace and bell -->
    <header class="mtop" v-if="isMobile">
      <button type="button"
              class="mtop__btn"
              @click="drawerOpen = true"
              :aria-label="$t('app_nav.open_menu')"
              qid="side-nav-toggle"
      >
        <font-awesome-icon icon="bars"/>
      </button>
      <WorkspaceSwitcher class="mtop__ws"/>
      <button type="button"
              class="mtop__btn mtop__bell"
              @click="togglePanel('notifications')"
              :aria-label="$t('notifications.page_title')"
      >
        <font-awesome-icon icon="bell"/>
        <span class="sb-badge" v-if="unreadCount">{{ unreadLabel }}</span>
      </button>
    </header>

    <div class="sb-drawer-backdrop" v-if="isMobile && drawerOpen" @click="drawerOpen = false"></div>

    <aside class="sb" :class="{ 'sb--rail': compact, 'sb--drawer-open': drawerOpen }">
      <button type="button"
              class="sb-edge"
              v-if="!isMobile"
              @click="toggleRail"
              :aria-label="collapsed ? $t('app_nav.expand_menu') : $t('app_nav.collapse_menu')"
              qid="side-nav-collapse"
      >
        <font-awesome-icon :icon="collapsed ? 'chevron-right' : 'chevron-left'"/>
      </button>
      <button type="button"
              class="sb-drawer-close"
              v-if="isMobile"
              @click="drawerOpen = false"
              :aria-label="$t('app_nav.close_menu')"
      >
        <font-awesome-icon icon="times"/>
      </button>

      <WorkspaceSwitcher v-if="!isMobile" :compact="compact"/>

      <!-- The menu scrolls when it doesn't fit; arrows and fades show there is more (US 9365) -->
      <div class="sb-scroll" :class="{ 'sb-scroll--up': canScrollUp, 'sb-scroll--down': canScrollDown }">
        <button v-if="canScrollUp"
                type="button"
                class="sb-scroll__btn sb-scroll__btn--up"
                tabindex="-1"
                :aria-label="$t('app_nav.scroll_up')"
                qid="side-nav-scroll-up"
                @click="scrollNav(-1)"
        >
          <font-awesome-icon icon="chevron-up"/>
        </button>
        <SidebarNav ref="nav" :compact="compact" class="sb__nav"/>
        <button v-if="canScrollDown"
                type="button"
                class="sb-scroll__btn sb-scroll__btn--down"
                tabindex="-1"
                :aria-label="$t('app_nav.scroll_down')"
                qid="side-nav-scroll-down"
                @click="scrollNav(1)"
        >
          <font-awesome-icon icon="chevron-down"/>
        </button>
      </div>

      <div class="sb-util">
        <LastTaskButton :compact="compact"/>
        <router-link class="sb-item"
                     to="/maintenance"
                     v-if="isGranted(permissions.MAINTENANCE_VIEW)"
                     :aria-label="compact ? $t('maintenance.title') : null"
                     qid="navbar-maintenance-link"
        >
          <font-awesome-icon icon="cog" class="sb-item__icon"/>
          <span class="sb-item__text">{{ $t('maintenance.title') }}</span>
        </router-link>
        <button type="button"
                class="sb-item"
                :class="{ 'sb-item--open': activityOpen }"
                @click="togglePanel('activity')"
                :aria-label="compact ? $t('top_nav.activity_log') : null"
                qid="navbar-activity-link"
        >
          <font-awesome-icon icon="wave-square" class="sb-item__icon"/>
          <span class="sb-item__text">{{ $t('top_nav.activity_log') }}</span>
        </button>
        <button type="button"
                class="sb-item"
                :class="{ 'sb-item--open': notificationsOpen }"
                @click="togglePanel('notifications')"
                :aria-label="compact ? $t('notifications.page_title') : null"
                qid="navbar-notifications-toggle"
        >
          <font-awesome-icon icon="bell" class="sb-item__icon"/>
          <span class="sb-item__text">{{ $t('notifications.page_title') }}</span>
          <span class="sb-badge" v-if="unreadCount">{{ unreadLabel }}</span>
        </button>
        <LanguageMenu :compact="compact"/>
      </div>

      <router-link class="sb-trial"
                   v-if="trialStatus && !compact"
                   :class="{ 'sb-trial--expired': trialStatus === 'expired' }"
                   :to="{ name: 'clientProfile', params: { slug: getClient.slug }, query: { tabindex: 1 }}"
      >
        <font-awesome-icon icon="clock"/>
        <span v-if="trialStatus === 'expired'">{{ $t('profile.client.billing.trial_expired_label') }}</span>
        <span v-else>{{ $t('app_nav.trial_expires', { days: trialDays }) }}</span>
      </router-link>

      <AccountMenu :compact="compact"/>
    </aside>

    <NotificationsPanel v-if="notificationsOpen"
                        :unread-count="unreadCount"
                        :unread-capped="unreadCapped"
                        @close="openPanel = null"
                        @changed="loadUnreadCount"
    />
    <ActivityPanel v-if="activityOpen" @close="openPanel = null"/>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import permissionCheck from '@/util/ACL/permissionCheck';
import WorkspaceSwitcher from '@/components/navigation/WorkspaceSwitcher.vue';
import SidebarNav from '@/components/navigation/SidebarNav.vue';
import NotificationsPanel from '@/components/navigation/NotificationsPanel.vue';
import ActivityPanel from '@/components/navigation/ActivityPanel.vue';
import LanguageMenu from '@/components/navigation/LanguageMenu.vue';
import AccountMenu from '@/components/navigation/AccountMenu.vue';
import LastTaskButton from '@/components/navigation/LastTaskButton.vue';
import { railPreference, setRail, isRail } from '@/util/sidebarRail.js';

const MOBILE_WIDTH = 768;
const TABLET_WIDTH = 1200;
const COUNT_INTERVAL = 60 * 1000;

export default {
  name: 'AppSidebar',
  components: {
    WorkspaceSwitcher,
    LastTaskButton,
    SidebarNav,
    NotificationsPanel,
    ActivityPanel,
    LanguageMenu,
    AccountMenu
  },
  data: function() {
    return {
      width: window.innerWidth,
      collapsed: false,
      drawerOpen: false,
      // The notifications or activity log panel; one at a time
      openPanel: null,
      unreadCount: 0,
      unreadCapped: false,
      countTimer: null,
      railObserver: null,
      canScrollUp: false,
      canScrollDown: false,
      navObserver: null,
      navContentObserver: null,
    }
  },
  computed: {
    getClient() {
      return this.$store.state.client
    },
    getReloadCount() {
      return this.$store.state.reloadCount
    },
    notificationsOpen() {
      return this.openPanel === 'notifications';
    },
    activityOpen() {
      return this.openPanel === 'activity';
    },
    isMobile() {
      return this.width < MOBILE_WIDTH;
    },
    // Rail on desktop when chosen (or set by a page), always on tablet; never on mobile (drawer instead)
    compact() {
      if (this.isMobile) {
        return false;
      }
      return this.collapsed || this.width < TABLET_WIDTH;
    },
    unreadLabel() {
      return this.unreadCapped ? '99+' : this.unreadCount;
    },
    trialStatus() {
      if (this.getClient.subscription_status === 'trial') {
        return 'trial';
      }
      return this.getClient.subscription_status === 'trial_expired' ? 'expired' : null;
    },
    trialDays() {
      let moment = require('moment');
      return moment(this.getClient.trial_ends_at).diff(moment(), 'days') + 1;
    }
  },
  watch: {
    getClient: function(value, oldValue) {
      if (value && value.slug && (!oldValue || value.slug !== oldValue.slug)) {
        this.loadUnreadCount();
      }
    },
    getReloadCount: function() {
      this.loadUnreadCount();
    },
    $route: function() {
      this.drawerOpen = false;
      this.openPanel = null;
      this.$nextTick(this.revealActiveItem);
    },
    compact: function(value) {
      document.body.classList.toggle('app-nav-rail', value);
    },
    isMobile: function(value) {
      document.body.classList.toggle('app-nav-mobile', value);
    }
  },
  methods: {
    isGranted(permission) {
      return permissionCheck(permission)
    },
    toggleRail() {
      setRail(!this.collapsed, true);
    },
    togglePanel(panel) {
      this.openPanel = this.openPanel === panel ? null : panel;
      this.drawerOpen = false;
    },
    loadUnreadCount() {
      let self = this;
      if (!this.getClient || !this.getClient.slug) {
        return;
      }
      piincHttp.get('users/me/notifications/count').then(function(response) {
        self.unreadCount = response.data.unread_count;
        self.unreadCapped = !!response.data.unread_count_capped;
      }, function() {});
    },
    onFocus() {
      this.loadUnreadCount();
    },
    onResize() {
      this.width = window.innerWidth;
    },
    // "[" collapses and expands the sidebar, unless the user is typing
    onKeydown(event) {
      if (event.key !== '[' || event.ctrlKey || event.metaKey || event.altKey || this.isMobile) {
        return;
      }
      let target = event.target;
      if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].indexOf(target.tagName) !== -1)) {
        return;
      }
      this.toggleRail();
    },
    syncRail() {
      this.collapsed = isRail();
    },
    navElement() {
      return this.$refs.nav ? this.$refs.nav.$el : null;
    },
    // Show the arrows only while there is more menu above or below
    updateNavScroll() {
      let nav = this.navElement();
      let scrollable = nav && getComputedStyle(nav).overflowY !== 'visible' && nav.scrollHeight > nav.clientHeight + 1;

      this.canScrollUp = !!scrollable && nav.scrollTop > 0;
      this.canScrollDown = !!scrollable && nav.scrollTop + nav.clientHeight < nav.scrollHeight - 1;
    },
    scrollNav(direction) {
      let nav = this.navElement();
      if (nav) {
        nav.scrollBy({ top: direction * Math.round(nav.clientHeight * 0.6), behavior: 'smooth' });
      }
    },
    // Keep the current page's menu item in sight after navigating
    revealActiveItem() {
      let nav = this.navElement();
      let active = nav && nav.querySelector('.router-link-exact-active, .router-link-active.sb-item--child');
      if (active) {
        let top = active.offsetTop - nav.offsetTop;
        if (top < nav.scrollTop || top + active.offsetHeight > nav.scrollTop + nav.clientHeight) {
          nav.scrollTop = top - nav.clientHeight / 2;
        }
      }
      this.updateNavScroll();
    }
  },
  created() {
    setRail(railPreference(), false);
    this.loadUnreadCount();
    this.countTimer = setInterval(this.loadUnreadCount, COUNT_INTERVAL);
    window.addEventListener('focus', this.onFocus);
    window.addEventListener('resize', this.onResize);
    document.addEventListener('keydown', this.onKeydown);
  },
  mounted() {
    // Pages may switch the rail on themselves (the #app class), so follow that class
    this.syncRail();
    this.railObserver = new MutationObserver(this.syncRail);
    this.railObserver.observe(document.getElementById('app'), { attributes: true, attributeFilter: ['class'] });
    document.body.classList.toggle('app-nav-rail', this.compact);
    document.body.classList.toggle('app-nav-mobile', this.isMobile);

    // Menu groups open and close, and the window resizes, so watch the menu's size
    let nav = this.navElement();
    if (nav) {
      nav.addEventListener('scroll', this.updateNavScroll, { passive: true });
      if (window.ResizeObserver) {
        this.navObserver = new ResizeObserver(this.updateNavScroll);
        this.navObserver.observe(nav);
      }
      this.navContentObserver = new MutationObserver(() => this.$nextTick(this.updateNavScroll));
      this.navContentObserver.observe(nav, { childList: true, subtree: true });
      this.$nextTick(this.revealActiveItem);
    }
  },
  beforeDestroy() {
    clearInterval(this.countTimer);
    window.removeEventListener('focus', this.onFocus);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('keydown', this.onKeydown);
    if (this.railObserver) {
      this.railObserver.disconnect();
    }
    if (this.navObserver) {
      this.navObserver.disconnect();
    }
    if (this.navContentObserver) {
      this.navContentObserver.disconnect();
    }
    let nav = this.navElement();
    if (nav) {
      nav.removeEventListener('scroll', this.updateNavScroll);
    }
    document.body.classList.remove('app-nav-rail', 'app-nav-mobile');
  }
}
</script>
